"use client"

import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-[3px] border border-transparent text-[13px] font-bold whitespace-nowrap transition-all outline-none select-none cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "bg-[#0078C8] text-white border-[#0078C8] hover:bg-[#006BB3] hover:border-[#006BB3] active:bg-[#005a99]",
        create:
          "bg-[#00823C] text-white border-[#00823C] hover:bg-[#006B31] hover:border-[#006B31] active:bg-[#005a26]",
        outline:
          "bg-white text-[#0078C8] border-[#A6A9B0] hover:bg-gray-50 active:bg-gray-100",
        secondary:
          "bg-white text-[#0078C8] border-[#A6A9B0] hover:bg-gray-50 active:bg-gray-100",
        ghost:
          "bg-transparent text-[#0078C8] border-transparent hover:bg-gray-100",
        destructive:
          "bg-[#CC0000]/10 text-[#CC0000] border-transparent hover:bg-[#CC0000]/20",
        link: "text-[#0078C8] border-transparent underline-offset-4 hover:underline font-bold",
        pill: "bg-white text-[#0078C8] border-[#CCCED2] rounded-[48px] hover:bg-gray-50",
      },
      size: {
        default: "h-8 gap-1.5 px-3",
        xs: "h-6 gap-1 px-2 text-xs",
        sm: "h-7 gap-1 px-2.5 text-[13px]",
        lg: "h-9 gap-1.5 px-4",
        icon: "size-8",
        "icon-xs": "size-6",
        "icon-sm": "size-7",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
